import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { PartyPopperIcon } from './Icons';
import './QuickStartGuide.css';
import SiteFooter from './SiteFooter';

const QuickStartGuide: React.FC = () => {
  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);

    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });

      resizeObserver.observe(header);
      window.addEventListener('resize', updateHeaderHeight);

      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeaderHeight);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  return (
    <div className="quick-start-guide-page">
      <Header subtitle="Quick Start" showNavigation={true} />

      <main className="container">
        <div className="main-content">
          <div className="page-header">
            <h1>Quick Start</h1>
            <p className="page-subtitle">
              Here are the detailed 5 steps required to build your Trustline'd dApp
            </p>
          </div>

          <section className="guide-section">
            <h2>Code Integration Overview</h2>
            <p>
              Integrating Trustline is simple and quick. You'll add:
            </p>
            <ul>
              <li>
                <strong>Smart Contract</strong>: An on-chain check (<code>requireTrustline()</code>) in the method you're protecting.
              </li>
              <li>
                <strong>Frontend</strong>: The corresponding off-chain validation call (<code>trustline.validate()</code>) before creating the transaction.
              </li>
            </ul>
            <p>
              The exact data required depends on your policies and transactions. See our{' '}
              <a href="https://github.com/Trustline-id/evmsdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                EVMSDK
              </a>{' '}
              and{' '}
              <a href="https://github.com/Trustline-id/websdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                WebSDK
              </a>{' '}
              repositories for more details.
            </p>
          </section>

          <section className="guide-section step-section">
            <div className="step-header">
              <div className="step-number">1</div>
              <h2>Add Trustline to your smart contract</h2>
            </div>
            <p>
              Integrate Trustline into your smart contracts using our EVM SDK NPM package, or pick one of our off-the-shelf protected contracts from our repository. The EVM SDK allows you to protect your smart contract methods with just one line of code.
            </p>

            <h3>Install EVMSDK</h3>
            <p>Install the Trustline EVMSDK via NPM:</p>
            <pre className="code-block">
              <code>npm install '@trustline.id/evmsdk'</code>
            </pre>

            <h3>Add Trustline Protection to Your Contract</h3>
            <p>Add Trustline protection to your smart contracts easily:</p>
            <ol>
              <li>Import <code>Trustlined.sol</code>.</li>
              <li>Inherit from <code>Trustlined</code>.</li>
              <li>Allow passing a <strong>Validation Engine</strong> address to your constructor.</li>
              <li>Call <code>requireTrustline()</code> within protected methods.</li>
            </ol>
            <pre className="code-block">
              <code>{`import {Trustlined} from "@trustline.id/evmsdk/contracts/Trustlined.sol";

contract MySmartContract is Trustlined {
    constructor(
        address ValidationEngine
    ) Trustlined(ValidationEngine, address(0)) {}

    function receivePayment(address recipient) external payable {
        requireTrustline();
        (bool sent, ) = recipient.call{value: msg.value}("");
        require(sent, "Ether transfer failed");
    }
}`}</code>
            </pre>
            <div className="note-callout">
              <strong>Note</strong>
              <p>
                <code>requireTrustline(address[] addresses)</code> also allows to provide an array of addresses that will be verified using on-chain oracles.
              </p>
            </div>

            <p>
              For more information, visit the{' '}
              <a href="https://www.npmjs.com/package/@trustline.id/evmsdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                EVMSDK on NPM
              </a>{' '}
              or{' '}
              <a href="https://github.com/Trustline-id/evmsdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                EVMSDK on GitHub
              </a>
              .
            </p>
          </section>

          <section className="guide-section step-section">
            <div className="step-header">
              <div className="step-number">2</div>
              <h2>Select your Blockchain</h2>
            </div>
            <p>
              For your smart contract deployment, look-up the <strong>Validation Engine</strong> address corresponding to your target blockchain.
            </p>
            <p>
              Visit our{' '}
              <Link to="/implementation-list" className="inline-link">
                Supported Blockchains
              </Link>{' '}
              page to find the right contract address for your blockchain, copy the address of the latest version and use it when initializing your contract's constructor during deployment.
            </p>
            <div className="note-callout">
              <strong>Note</strong>
              <p>
                The Validation Engine is deployed by Trustline and contains the validation logic. A Validation Engine proxy is auto-deployed with each protected smart contract. You do not need to do anything.
              </p>
            </div>
            <p>
              <Link to="/implementation-list" className="inline-link">
                View Supported Blockchains {'>'}
              </Link>
            </p>
          </section>

          <section className="guide-section step-section">
            <div className="step-header">
              <div className="step-number">3</div>
              <h2>Set your policies and activate protection</h2>
            </div>
            <p>
              Once your contract is deployed, use our onboarding flow to configure security policies and activate Trustline protection for your dApp. Map policies to specific contract methods and customize protection rules.
            </p>
            <p>
              Currently, we offer an <strong>Email OTP-based Policy</strong> free of charge as part of our Discovery plan. This provides essential protection for your smart contracts. Any Trustline integration is future-proof, and you can upgrade to more advanced plans that include customizable policies with additional security checks, enhanced verification methods, and advanced compliance features.
            </p>
            <p>
              <a 
                href="https://onboarding.trustline.id/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-link"
              >
                Go to Onboarding Portal {'>'}
              </a>
            </p>
          </section>

          <section className="guide-section step-section">
            <div className="step-header">
              <div className="step-number">4</div>
              <h2>Connect Trustline to your UI</h2>
            </div>
            <p>
              Integrate Trustline validation into your frontend using our Web SDK NPM package. This SDK handles user interaction, policy checks, and transaction validation seamlessly.
            </p>

            <h3>Install WebSDK</h3>
            <h4>React / Next.js / Vue.js / Angular</h4>
            <p>Install the Trustline Web SDK:</p>
            <pre className="code-block">
              <code>npm install '@trustline.id/websdk'</code>
            </pre>
            <p>Import Trustline's validation method into your JavaScript/TypeScript files:</p>
            <pre className="code-block">
              <code>{'import { trustline } from "@trustline.id/websdk";'}</code>
            </pre>

            <h4>Vanilla JavaScript</h4>
            <p>Include Trustline's Web SDK in your HTML file via a <code>&lt;script&gt;</code> tag:</p>
            <pre className="code-block">
              <code>{'<script src="https://cdn.jsdelivr.net/npm/@trustline.id/websdk@latest/dist/trustline.umd.min.js"></script>'}</code>
            </pre>

            <h3>Initialize Trustline SDK</h3>
            <p>Initialize the library using your <strong>Client ID</strong>:</p>
            <pre className="code-block">
              <code>{`trustline.init({
    clientId: "YOUR_TRUSTLINE_CLIENT_ID"
});`}</code>
            </pre>
            <div className="note-callout">
              <strong>Note</strong>
              <p>
                You will find your Client ID in the{' '}
                <a href="https://onboarding.trustline.id/" target="_blank" rel="noopener noreferrer" className="inline-link">Onboarding Portal</a>, below your email address.
              </p>
              <img src="/ClientID.png" alt="Client ID location in the onboarding portal" className="client-id-image" />
            </div>

            <h3>Implement the Validation Call</h3>
            <p>
              Wrap your transaction creation logic with Trustline's validation function:
            </p>
            <pre className="code-block">
              <code>{`const response = await trustline.validate({
    chainId: '1',
    senderAddress: '0x...',
    contractAddress: '0x...',
    nativeAmount: '<amount in wei>',
    data: {
        functionPrototype: 'receivePayment(address)',
        args: ['0x...']
    }
});

if ('result' in response && response.result.status === 'approved') {
    // Execute your transaction here
    console.log('Transaction approved!', response.result.certId);
} else if ('result' in response && response.result.status === 'rejected') {
    alert(\`Transaction rejected: \${response.result.reason}\`);
} else if ('error' in response) {
    alert(\`Service error: \${response.error.message}. Please try again later.\`);
}`}</code>
            </pre>
            <div className="note-callout">
              <strong>Note</strong>
              <p>
                The <code>data</code> field can also be a raw hex string corresponding to the encoded function call data (<code>data: '0x...'</code>) instead of structured data.
              </p>
            </div>

            <p>
              For more information, visit the{' '}
              <a href="https://www.npmjs.com/package/@trustline.id/websdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                WebSDK on NPM
              </a>{' '}
              or{' '}
              <a href="https://github.com/Trustline-id/websdk" target="_blank" rel="noopener noreferrer" className="inline-link">
                WebSDK on GitHub
              </a>
              .
            </p>
          </section>

          <section className="guide-section step-section">
            <div className="step-header">
              <div className="step-number">5</div>
              <h2>And... voila!</h2>
            </div>
            <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              You can now share your UI with your users, your smart contract service is protected by Trustline.
              <PartyPopperIcon size={48} />
            </p>
          </section>

        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default QuickStartGuide;
